import { FormDivider, Link } from '@hex/calculators-shared/components';
import { ChartLegend } from '@hex/calculators-shared/components/DonutChart/ChartLegend';
import { Donut } from '@hex/calculators-shared/components/DonutChart/Donut';
import { INTEREST_RATE_CALCULATION_DATE } from '@hex/calculators-shared/constants';
import { ResultWhiteBox } from '@hex/calculators-shared/layout';
import { labelWeight } from '@hex/calculators-shared/types/affordability';
import { formatCalculationDate, formatMoney, formatNumber } from '@hex/calculators-shared/utils';
import { track } from '@hex/shared/tracking';
import { Button, Flex, tokens, Typography, View, ThemeRoot } from '@sapphire-react/core';
import { useEffect, useRef, useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { MINIMUM_LOAN_VALUE } from '../../../constants';
import { useAffordabilityCalculator } from '../../hooks/useAffordabilityCalculator';
import { AffordabilityResponse } from '../../types';
import ErrorCard from './ErrorCard';
import { Header } from './Header';
import LoanAdjust from './LoanAdjust';

type Props = {
  affordabilityResponseData: AffordabilityResponse | null;
  hasError: boolean;
  isDisabled: boolean;
  loading: boolean;
  onError: () => void;
};

export function Results({ affordabilityResponseData, hasError, isDisabled }: Props) {
  const [affordability, setAffordability] = useState<AffordabilityResponse | null>(null);
  const [initialLoanAmount, setInitialLoanAmount] = useState<number>(0);
  const { calculationResults } = useAffordabilityCalculator(initialLoanAmount, isDisabled);

  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const redirectUri = t('results.donutChart.maturity.tooltip.url'); // The redirect to the loan monthly cost calculator
  const donutSize = 238;

  useEffect(() => {
    if (affordabilityResponseData) {
      if (affordabilityResponseData.loanAmount > 0 && !isDisabled) ref.current?.scrollIntoView({ behavior: 'smooth' });

      setAffordability(affordabilityResponseData);
      setInitialLoanAmount(affordabilityResponseData.loanAmount);
    } else if (isDisabled) {
      setInitialLoanAmount(0);
      setAffordability(null);
    }
  }, [affordabilityResponseData, isDisabled]);

  const chartItems = [
    {
      value: calculationResults.amortization,
      displayLabel: formatMoney(calculationResults.amortization ?? 0, { currency: '€' }),
      color: tokens.color.backgroundDecorative1.value,
      legendTitle: t('results.donutChart.amortization.label'),
      borderRadius: 0,
      contextualHelpContent: isDisabled ? undefined : (
        <Typography.Body size={'sm'} aria-live={'polite'}>
          {t('results.donutChart.amortization.tooltip')}
        </Typography.Body>
      ),
      testId: 'amortization-value',
    },
    {
      value: calculationResults.interestPayment,
      displayLabel: formatMoney(calculationResults.interestPayment ?? 0, { currency: '€' }),
      color: tokens.color.backgroundDecorative3.value,
      legendTitle: t('results.donutChart.interestPayment.label'),
      borderRadius: 0,
      testId: 'interest-payment-value',
    },
  ];

  const loanDetails = [
    {
      value: calculationResults.loanAmount,
      displayLabel: formatMoney(calculationResults.loanAmount ?? 0, { currency: '€' }),
      legendTitle: t('results.donutChart.loanAmount.label'),
      testId: 'loan-amount-value',
      labelWeight: 'bold' as labelWeight,
    },
    {
      value: calculationResults.maturityYears,
      displayLabel: `${formatNumber(calculationResults.maturityYears)} ${t('years')}`,
      legendTitle: t('results.donutChart.maturity.label'),
      labelWeight: 'bold' as labelWeight,
      contextualHelpContent: isDisabled ? undefined : (
        <>
          <Typography.Body size={'sm'} aria-live={'polite'}>
            {t('results.donutChart.maturity.tooltip.text')}
          </Typography.Body>
          <Typography.Body size={'md'}>
            <Link marginTop={tokens.size.spacingMd}>
              <a href={redirectUri} target={'_blank'}>
                {t('results.donutChart.maturity.tooltip.link')}
              </a>
            </Link>
          </Typography.Body>
        </>
      ),
      testId: 'maturity-value',
    },
    {
      value: calculationResults.interestRate,
      displayLabel: `${formatNumber(calculationResults.interestRate)} %`,
      legendTitle: t('results.donutChart.interestRate.label'),
      labelWeight: 'bold' as labelWeight,
      contextualHelpContent: isDisabled ? undefined : (
        <>
          <Typography.Body size={'sm'} aria-live={'polite'}>
            {t('results.donutChart.interestRate.tooltip.label', {
              date: formatCalculationDate(INTEREST_RATE_CALCULATION_DATE),
            })}
          </Typography.Body>
          <Typography.Body size={'md'}>
            <Link marginTop={tokens.size.spacingMd}>
              {}
              <a href={redirectUri} target={'_blank'}>
                {t('results.donutChart.interestRate.tooltip.link')}
              </a>
            </Link>
          </Typography.Body>
        </>
      ),
      testId: 'interest-rate-value',
    },
  ];
  const total = chartItems.reduce((prev, curr) => prev + (curr.value ?? 0), 0);
  const segments = total
    ? chartItems.map((item) => ({
        percentage: ((item.value ?? 0) * 100) / total,
        color: item.color,
        formattedValue: item.displayLabel,
        label: item.legendTitle,
      }))
    : [];

  return (
    <>
      <ErrorCard isHidden={!hasError} />
      <View position={'relative'} ref={ref} aria-disabled={isDisabled} isHidden={hasError}>
        <Header loanAmount={initialLoanAmount} isDisabled={isDisabled} />
        <Typography.Heading
          level={5}
          elementType={'h3'}
          textAlign={'center'}
          marginBottom={24}
          marginTop={{ base: 24, sm: 32 }}
          marginLeft={{ base: 12, sm: 24, md: 32 }}
          marginRight={{ base: 12, sm: 24, md: 32 }}
        >
          {t('results.donutChart.heading')}
        </Typography.Heading>
        <View marginLeft={{ base: 12, sm: 24, md: 32 }} marginRight={{ base: 12, sm: 24, md: 32 }}>
          <ResultWhiteBox border={'none'} borderRadius={tokens.size.radiusSm.value} paddingTop={0}>
            {/* for screen reader to announce that results card is now active  */}
            {/* <div aria-live={'polite'}
              //    aria-disabled={disabled}
       >
        {!disabled && (
          <>
            <AriaOnlyText>
              {`Den månatliga betalningen för lånet skulle bli ${getChartTotal()} kr/månad. Av detta belopp är amortering ${getAmortizationValue()} kr vilket motsvarar ${getAmortizationSubtitle()} av lånet. Räntekostnaden skulle bli ${getInterestPaymentValue()} kr och beräknas med ${interestRate} % ränta.`}
            </AriaOnlyText>
            <AriaOnlyText>
              {`Dessa beräkningar är baserade på räntor i nuvarande marknadsläge, räntor efter ränteavdrag samt en bindningstid på ${interestRates[selectedInterestRate].text}. Det finns tre ändringar du kan göra till beräkningarna. Du kan ändra räntan från att vara i nuvarande marknadsläge till en du själv anger, du kan räkna med eller utan ränteavdrag, och du kan ändra bindningstid för lånet.`}
            </AriaOnlyText>
          </>
        )}
      </div> */}
            <View marginTop={{ base: 13, sm: 19 }} marginBottom={{ base: 30, sm: 20 }}>
              <Donut
                segments={segments}
                donutHeight={donutSize}
                donutWidth={donutSize}
                circleSizeX={donutSize}
                circleSizeY={donutSize}
                strokeWidth={2.6}
                interactive
              >
                <Typography.Heading data-test-id={'donut-total'} level={4} fontSize={24}>
                  {formatMoney(calculationResults.monthlyInstallment ?? 0, { currency: '€' })}
                </Typography.Heading>
                <Typography.Body size={'sm'}>{t('results.donutChart.monthlyInstallment')}</Typography.Body>
              </Donut>
            </View>
            <Flex
              flexDirection={'column'}
              width={'100%'}
              paddingLeft={{ base: tokens.size.spacing2xs, sm: tokens.size.spacing2xs, md: tokens.size.spacingLg }}
              paddingRight={{ base: tokens.size.spacing2xs, sm: tokens.size.spacing2xs, md: tokens.size.spacingLg }}
              marginTop={{ base: -38, sm: -28 }}
              UNSAFE_style={{ boxSizing: 'border-box' }}
            >
              {chartItems.map((item, index) => (
                <Fragment key={`legend-${index}`}>
                  <ChartLegend
                    chartItem={item}
                    currency={'€'}
                    helpLabel={item.contextualHelpContent ? t('help') : ''}
                  />
                </Fragment>
              ))}
              <FormDivider marginBottom={tokens.size.spacing2xs} marginTop={tokens.size.spacing2xs} />
              {loanDetails.map((item, index) => (
                <Fragment key={`legend-${index}`}>
                  <ChartLegend
                    chartItem={item}
                    withLegendIndicator={false}
                    currency={'€'}
                    helpLabel={item.contextualHelpContent ? t('help') : ''}
                  />
                </Fragment>
              ))}
            </Flex>
          </ResultWhiteBox>

          <View marginTop={24}>
            <ResultWhiteBox border={'none'} borderRadius={tokens.size.radiusSm.value} display={'block'}>
              <LoanAdjust
                minValue={MINIMUM_LOAN_VALUE}
                maxValue={affordability?.loanAmount}
                loanAmount={initialLoanAmount}
                setLoanAmount={setInitialLoanAmount}
                isDisabled={isDisabled}
              />
            </ResultWhiteBox>
          </View>
          <Flex
            marginTop={tokens.size.spacingMd}
            marginBottom={tokens.size.spacing2xl}
            flexDirection={'column'}
            alignItems={'center'}
            UNSAFE_style={{ paddingBottom: isDisabled ? tokens.size.spacingXl.value : 0 }}
          >
            <Typography.Subheading size={'sm'} textAlign={'center'}>
              {t('results.footer.label')}
            </Typography.Subheading>
            <ThemeRoot variant={'secondary'}>
              <Button
                variant={'secondary'}
                elementType={'a'}
                href={`${redirectUri}?amount=${initialLoanAmount}`}
                target={'_blank'}
                name={t('results.footer.ctaButton')}
                data-test-id={'cost-calculator-redirect'}
                data-tracking-id={'Housing:Mortgage:Affordability Calculator'}
                isDisabled={isDisabled}
                aria-label={t('results.footer.ctaButton')}
                marginTop={tokens.size.spacingLg}
                onPress={() => {
                  track('Housing:Mortgage:Affordability Calculator');
                }}
              >
                {t('results.footer.ctaButton')}
              </Button>
            </ThemeRoot>
          </Flex>
        </View>
      </View>
    </>
  );
}
